import React, { useState, useEffect, useRef } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * RotatingTestimonials — Batch 2G component.
 *
 * Renders a rotating carousel of 2-5 real testimonials with:
 *   • auto-rotate every AUTO_MS (pause-on-hover)
 *   • prev / next controls
 *   • dot indicator strip
 *   • subtle fade transition
 *   • data-testids on every interactive element
 *
 * Two visual variants:
 *   • variant="light" — used inside InternationalCity.jsx (light bg card)
 *   • variant="dark"  — used inside OnlineCountry.jsx (dark hero-style band)
 *
 * Props:
 *   items:   [{ n, c, t }, ...]   required
 *   variant: "light" | "dark"     default "light"
 *   heading: optional string      shown above the carousel
 */
const AUTO_MS = 6500;

export default function RotatingTestimonials({ items, variant = "light", heading }) {
    const [idx, setIdx] = useState(0);
    const [paused, setPaused] = useState(false);
    const timerRef = useRef(null);

    const count = items?.length || 0;
    useEffect(() => {
        if (count <= 1 || paused) return;
        timerRef.current = setTimeout(() => setIdx((i) => (i + 1) % count), AUTO_MS);
        return () => clearTimeout(timerRef.current);
    }, [idx, count, paused]);

    if (count === 0) return null;

    const goto = (n) => setIdx(((n % count) + count) % count);
    const cur = items[idx];

    if (variant === "dark") {
        return (
            <section
                className="py-16 bg-[#0F172A] text-white relative overflow-hidden"
                data-testid="rotating-testimonials-dark"
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
            >
                <div className="absolute inset-0 bg-grid opacity-10"></div>
                <div className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
                    <div className="flex justify-center gap-1 mb-4 text-amber-400">
                        {Array.from({ length: 5 }).map((_, i) => (
                            <Star key={i} className="w-5 h-5 fill-amber-400" />
                        ))}
                    </div>
                    <p
                        key={idx}
                        className="font-heading text-xl sm:text-2xl leading-relaxed text-blue-100 animate-fade"
                        data-testid="testimonial-quote"
                    >
                        &ldquo;{cur.t}&rdquo;
                    </p>
                    <div className="mt-6">
                        <div className="font-heading font-semibold" data-testid="testimonial-name">{cur.n}</div>
                        <div className="text-xs text-blue-300 uppercase tracking-[0.2em] mt-1" data-testid="testimonial-context">{cur.c}</div>
                    </div>

                    {count > 1 && (
                        <div className="mt-8 flex items-center justify-center gap-4">
                            <button
                                onClick={() => goto(idx - 1)}
                                className="p-2 rounded-full border border-blue-800 text-blue-200 hover:bg-blue-900 transition"
                                aria-label="Previous testimonial"
                                data-testid="testimonial-prev"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <div className="flex items-center gap-2">
                                {items.map((_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => goto(i)}
                                        className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-amber-400" : "w-1.5 bg-blue-700 hover:bg-blue-500"}`}
                                        aria-label={`Show testimonial ${i + 1}`}
                                        data-testid={`testimonial-dot-${i}`}
                                    />
                                ))}
                            </div>
                            <button
                                onClick={() => goto(idx + 1)}
                                className="p-2 rounded-full border border-blue-800 text-blue-200 hover:bg-blue-900 transition"
                                aria-label="Next testimonial"
                                data-testid="testimonial-next"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    )}
                </div>
            </section>
        );
    }

    // Light variant (default)
    return (
        <section
            className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10"
            data-testid="rotating-testimonials-light"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            {heading && <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">{heading}</h2>}
            <div className="rounded-xl bg-gradient-to-br from-brand-50 to-white border border-brand-200 p-8">
                <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-amber-500 fill-amber-500" />)}
                </div>
                <p
                    key={idx}
                    className="text-lg text-gray-800 italic animate-fade"
                    data-testid="testimonial-quote"
                >
                    &ldquo;{cur.t}&rdquo;
                </p>
                <div className="mt-3 text-sm text-gray-600">
                    <span className="font-semibold text-gray-900" data-testid="testimonial-name">{cur.n}</span>
                    <span> · </span>
                    <span data-testid="testimonial-context">{cur.c}</span>
                </div>

                {count > 1 && (
                    <div className="mt-6 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            {items.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => goto(i)}
                                    className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-brand-600" : "w-1.5 bg-brand-200 hover:bg-brand-400"}`}
                                    aria-label={`Show testimonial ${i + 1}`}
                                    data-testid={`testimonial-dot-${i}`}
                                />
                            ))}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => goto(idx - 1)}
                                className="p-1.5 rounded-full border border-brand-200 text-brand-700 hover:bg-brand-100 transition"
                                aria-label="Previous testimonial"
                                data-testid="testimonial-prev"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => goto(idx + 1)}
                                className="p-1.5 rounded-full border border-brand-200 text-brand-700 hover:bg-brand-100 transition"
                                aria-label="Next testimonial"
                                data-testid="testimonial-next"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Fade animation keyframes (scoped) */}
            <style>{`
                @keyframes fade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
                .animate-fade { animation: fade 350ms ease-out; }
            `}</style>
        </section>
    );
}
